import { useState } from "react";
import { Icon } from "@iconify/react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Checkbox } from "antd";
import { googleCompleteSignupApi } from "@/portals/patient/api/patientAuthApi";
import { toastError } from "@/shared/lib/toast";
import { AuthPasswordField, AuthTextField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { AuthHeading } from "@/shared/components/AuthHeading";
import { validateFields } from "@/shared/lib/validators";
import { CONFIRM_PASSWORD_RULES, PASSWORD_MISMATCH_MESSAGE, SIGNUP_PASSWORD_RULES } from "@/shared/lib/passwordRules";
import { cn } from "@/shared/lib/utils";
import { isPastDate } from "./PatientSignUpPage";
import { savePendingSsnPrompt } from "./ssnPrompt";
import { applyGoogleSession } from "./googleAuth";
import { RequiredMark } from "@/shared/components/RequiredMark";

interface GoogleCompleteState {
  signupToken: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * One time step for a new Google user: Google never shares a date of birth,
 * and uploads are checked against first name + date of birth, so it is asked
 * here before the account is created. A password is required too, so the same
 * email also signs in with email + password. Name comes pre-filled from Google.
 */
export function GoogleCompleteSignupPage() {
  const navigate = useNavigate();
  const routeState = useLocation().state as GoogleCompleteState | null;
  const [firstName, setFirstName] = useState(routeState?.firstName ?? "");
  const [lastName, setLastName] = useState(routeState?.lastName ?? "");
  const [dob, setDob] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reached only from GoogleCallbackPage; a reload loses the token, so start over.
  if (!routeState?.signupToken) {
    return <Navigate to="/patient/auth/signup" replace />;
  }
  const { signupToken, email } = routeState;

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { firstName, lastName, dob, password, confirmPassword, terms },
      {
        firstName: [{ required: true, message: "First name is required" }],
        lastName: [{ required: true, message: "Last name is required" }],
        dob: [
          { required: true, message: "Date of birth is required" },
          { validator: (value) => (isPastDate(String(value)) ? undefined : "Date of birth must be in the past") },
        ],
        password: SIGNUP_PASSWORD_RULES,
        confirmPassword: CONFIRM_PASSWORD_RULES,
        terms: [{ validator: (value) => (value ? undefined : "Please accept the terms") }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    // Same check as the email signup.
    if (password !== confirmPassword) {
      toastError(PASSWORD_MISMATCH_MESSAGE);
      return;
    }

    setLoading(true);
    const { data, error, code } = await googleCompleteSignupApi(
      signupToken, firstName, lastName, dob, password, confirmPassword,
    );
    setLoading(false);

    if (!data) {
      toastError(error ?? "Unable to create account.");
      if (code === "GOOGLE_SIGNUP_EXPIRED") navigate("/patient/auth/signup", { replace: true });
      return;
    }
    // Same optional SSN step an email signup gets on its first Dashboard visit.
    savePendingSsnPrompt(email);
    applyGoogleSession(data);
    navigate("/patient", { replace: true });
  };

  return (
    <div className="w-full">
      <AuthHeading
        title={<>Almost <span className="font-extrabold tracking-tight">done</span></>}
        subtitle="Confirm your details to finish creating your account"
        className="mb-9"
      />

      <form onSubmit={handleSubmit}>
        <div className="mb-5 grid grid-cols-2 gap-3">
          <AuthTextField
            required
            label="First Name"
            placeholder="John"
            autoComplete="given-name"
            icon="lucide:user-round"
            value={firstName}
            onChange={(v) => { setFirstName(v); clearError("firstName"); }}
            error={errors.firstName}
            disabled={loading}
            className="mb-0"
          />
          <AuthTextField
            required
            label="Last Name"
            placeholder="Doe"
            autoComplete="family-name"
            icon="lucide:user-round"
            value={lastName}
            onChange={(v) => { setLastName(v); clearError("lastName"); }}
            error={errors.lastName}
            disabled={loading}
            className="mb-0"
          />
        </div>

        <AuthTextField
          required
          label="Email address"
          icon="lucide:mail"
          value={email}
          onChange={() => {}}
          disabled
        />

        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">Date of Birth<RequiredMark /></label>
          <div
            inert={loading}
            className={cn(
              "flex h-[42px] items-center rounded-[14px] border bg-white px-4 hover:border-[#E4002B]/50",
              errors.dob ? "border-[#E4002B]" : "border-[#E5E7EB]",
              loading && "opacity-60",
            )}
          >
            <span className="mr-2.5 flex items-center gap-2.5">
              <Icon icon="lucide:id-card" className="h-4 w-4 text-[#374151]" />
              <span className="h-5 w-px shrink-0 bg-[#DADDE5]" />
            </span>
            <CalendarDateField
              value={dob}
              onChange={(v) => { setDob(v); clearError("dob"); }}
              placeholder="Select date of birth"
              disableToday
              bare
            />
          </div>
          {errors.dob && <p className="!mt-1 text-xs text-[#E4002B]">{errors.dob}</p>}
        </div>

        <AuthPasswordField
          required
          label="Password"
          placeholder="Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={password}
          onChange={(v) => { setPassword(v); clearError("password"); }}
          error={errors.password}
          disabled={loading}
        />

        <AuthPasswordField
          required
          label="Confirm Password"
          placeholder="Confirm Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={confirmPassword}
          onChange={(v) => { setConfirmPassword(v); clearError("confirmPassword"); }}
          error={errors.confirmPassword}
          disabled={loading}
        />

        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={terms}
              disabled={loading}
              onChange={(e) => { setTerms(e.target.checked); clearError("terms"); }}
              className="[&_.ant-checkbox]:!rounded-full [&_.ant-checkbox]:!border-[#E4002B]/50 [&_.ant-checkbox-checked]:!bg-[#E4002B]/50 [&_.ant-checkbox-checked]:!border-[#E4002B]/50 [&_.ant-checkbox-checked::after]:!border-white [&_.ant-checkbox-checked_.ant-checkbox-inner::after]:!border-white"
            />
            <span className="text-[13px] text-[#6F7280]">
              I agree to the{" "}
              {/* New tab: leaving this page would lose the one time sign up token. */}
              <Button
                variant="link"
                disabled={loading}
                onClick={() => window.open("/terms-of-use", "_blank", "noopener")}
                className="!h-auto !p-0 !font-semibold !text-[#E4002B] !underline !underline-offset-2 hover:!text-[#E4002B]"
              >
                Terms of Use
              </Button>{" "}
              and{" "}
              <Button
                variant="link"
                disabled={loading}
                onClick={() => window.open("/privacy-policy", "_blank", "noopener")}
                className="!h-auto !p-0 !font-semibold !text-[#E4002B] !underline !underline-offset-2 hover:!text-[#E4002B]"
              >
                Privacy Policy
              </Button>
            </span>
          </div>
          {errors.terms && <p className="!mt-1 text-[11px] text-[#E4002B]">{errors.terms}</p>}
        </div>

        <AuthSubmitButton loading={loading}>Create account</AuthSubmitButton>
      </form>
    </div>
  );
}
